import axios from "axios";
import { buildStaticOptions} from "../utils/misc";
import { getHeadersConfig } from "../utils/config";
import ErrorHandler from "../utils/ErrorHandler";
import {PageState,ResponseStatus,PlacementResponseDto,PlacementRecord,ErrorData, CLinkOwnerOption, CLinkOwnerResponseDto, CLinkRecord} from "../utils/types";
import StateHelper from "../utils/StateHelper";
import {CampaignRecord,ProductRecord,ProductsCampaignsDto} from "../utils/types";
import { joinURLs } from "../utils/urlUtil";

let abortControllerForSearchPlacements = new AbortController();
let abortControllerForSearchProducts = new AbortController();
let abortControllerForSearchCampaigns = new AbortController();
let abortControllerForSearchCLinkOwners = new AbortController();

export const CommonRequests = {
  searchPlacements: function (q : string, setPlacementsResponseDto : (placementsResponseDto:PlacementResponseDto)=>void, setPlacementsIsLoading: (placementsIsLoading:boolean)=>void): void {
    if (abortControllerForSearchPlacements) abortControllerForSearchPlacements.abort();
    abortControllerForSearchPlacements = new AbortController();

    setPlacementsIsLoading(true);

    let publishersAndSites = "";
    const pageState : PageState = StateHelper.getPageState();

    const reconstructed: string[] = [];
    if (pageState.publisherIds && pageState.publisherIds.length > 0) {
      pageState.publisherIds.forEach(pubId => {
        // Find matching siteIds for this publisher
        const matchingSites = pageState.siteIds || [];
        if (matchingSites.length > 0) {
          matchingSites.forEach(siteId => {
            reconstructed.push(`${pubId}:${siteId}`);
          });
        } else {
          reconstructed.push(pubId);
        }
      });
    }

    if (reconstructed.length > 0) {
      publishersAndSites = reconstructed.join(",");
    }

    const params = {
      "placementsInclude": publishersAndSites,
      "q": q
    };

    const url = joinURLs("2/dashboard/placements", process.env.REACT_APP_REPORTING_API_BASE_URL);
    const config = getHeadersConfig();

    const staticOptions = buildStaticOptions( q );

    const dto : PlacementResponseDto = {
      status : ResponseStatus.OK, 
      error : null,
      placementsResponse : staticOptions
    };
    setPlacementsResponseDto(dto);

    axios
      .get(url, {...config, signal: abortControllerForSearchPlacements.signal, params: params})
      .then(function (response: any) {
        let options: PlacementRecord[] = response.data.placements;
        
        // add the static search items to the start of the options returned from the server
        options = [...staticOptions, ...options];

        const dto : PlacementResponseDto = {
          status : ResponseStatus.OK, 
          error : null,
          placementsResponse : options
        };

        setPlacementsResponseDto(dto);
        setPlacementsIsLoading(false);
      })
      .catch(function (error : any) {
        if (!axios.isCancel(error)) {
          setPlacementsIsLoading(false);

          const isLoggedOut = ErrorHandler.handleAuthError(error);
          if (!isLoggedOut) {
            const errData : ErrorData = ErrorHandler.getErrorMessage(error);
            
            const errorItem: PlacementRecord = {} as PlacementRecord
            errorItem.apptapPublisherId = "ERROR";
            errorItem.apptapSiteId = "ERROR";
            errorItem.publisherPlacementId = "ERROR";
            errorItem.publisherPlacementName = "Unable to load Publisher Placements";

            const options: PlacementRecord[] = [...staticOptions, errorItem];

            const dto : PlacementResponseDto = {
              status : ResponseStatus.ERROR, 
              error : errData,
              placementsResponse : options
            };
            setPlacementsResponseDto(dto);
          }
        }
      });
  },

  /**
   * Search products only with a typeahead query (minimum 3 characters).
   * Uses the new /dashboard/products endpoint.
   */
  searchProducts: async (
    query: string,
    setProductsDto: (dto: ProductsCampaignsDto) => void,
    setIsLoading: (loading: boolean) => void
  ) => {
    // Don't search if query is less than 3 characters
    if (!query || query.length < 3) {
      const emptyDto: ProductsCampaignsDto = {
        status: ResponseStatus.OK,
        error: null,
        productsResponse: [],
        campaignsResponse: []
      };
      setProductsDto(emptyDto);
      return;
    }

    if (abortControllerForSearchProducts) abortControllerForSearchProducts.abort();
    abortControllerForSearchProducts = new AbortController();

    setIsLoading(true);

    const config = getHeadersConfig();

    try {
      const url = joinURLs("2/dashboard/products", process.env.REACT_APP_REPORTING_API_BASE_URL);
      const response: any = await axios.get(url, {
        params: { "q": query },
        ...config,
        signal: abortControllerForSearchProducts.signal
      });

      const prodResponse: ProductRecord[] = response.data.products || [];

      if (prodResponse) {
        prodResponse.sort((a, b) => {
          if (a.productId == null) return -1;
          if (b.productId == null) return 1;
          return a.productId.localeCompare(b.productId);
        });
      }

      const dto: ProductsCampaignsDto = {
        status: ResponseStatus.OK,
        error: null,
        productsResponse: prodResponse,
        campaignsResponse: []
      };

      setProductsDto(dto);
      setIsLoading(false);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setIsLoading(false);
        const isLoggedOut = ErrorHandler.handleAuthError(error);
        if (!isLoggedOut) {
          const errData: ErrorData = ErrorHandler.getErrorMessage(error);
          const dto: ProductsCampaignsDto = {
            status: ResponseStatus.ERROR,
            error: errData,
            productsResponse: [],
            campaignsResponse: []
          };
          setProductsDto(dto);
        }
      }
    }
  },

  /**
   * Search campaigns only with a typeahead query (minimum 3 characters).
   * Uses the new /dashboard/campaigns endpoint.
   */
  searchCampaigns: async (
    query: string,
    setCampaignsDto: (dto: ProductsCampaignsDto) => void,
    setIsLoading: (loading: boolean) => void
  ) => {
    // Don't search if query is less than 3 characters
    if (!query || query.length < 3) {
      const emptyDto: ProductsCampaignsDto = {
        status: ResponseStatus.OK,
        error: null,
        productsResponse: [],
        campaignsResponse: []
      };
      setCampaignsDto(emptyDto);
      return;
    }

    if (abortControllerForSearchCampaigns) abortControllerForSearchCampaigns.abort();
    abortControllerForSearchCampaigns = new AbortController();

    setIsLoading(true);

    const config = getHeadersConfig();

    try {
      const url = joinURLs("2/dashboard/campaigns", process.env.REACT_APP_REPORTING_API_BASE_URL);
      const response: any = await axios.get(url, {
        params: { "q": query },
        ...config,
        signal: abortControllerForSearchCampaigns.signal
      });

      const campResponse: CampaignRecord[] = response.data.campaigns || [];

      campResponse.sort((a: CampaignRecord, b: CampaignRecord) => {
        const aKey = (a.demandSourceId || '') + ":" + (a.campaignId || '');
        const bKey = (b.demandSourceId || '') + ":" + (b.campaignId || '');
        return aKey.localeCompare(bKey);
      });

      const dto: ProductsCampaignsDto = {
        status: ResponseStatus.OK,
        error: null,
        productsResponse: [],
        campaignsResponse: campResponse
      };

      setCampaignsDto(dto);
      setIsLoading(false);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setIsLoading(false);
        const isLoggedOut = ErrorHandler.handleAuthError(error);
        if (!isLoggedOut) {
          const errData: ErrorData = ErrorHandler.getErrorMessage(error);
          const dto: ProductsCampaignsDto = {
            status: ResponseStatus.ERROR,
            error: errData,
            productsResponse: [],
            campaignsResponse: []
          };
          setCampaignsDto(dto);
        }
      }
    }
  },

  searchCLInkOwners: async (q : String) => {
    if (abortControllerForSearchCLinkOwners) abortControllerForSearchCLinkOwners.abort();
    abortControllerForSearchCLinkOwners = new AbortController();

    const params = { q };
    const config = getHeadersConfig();
    let dto : CLinkOwnerResponseDto;

    try {
      const url = joinURLs("2/dashboard/clinks", process.env.REACT_APP_REPORTING_API_BASE_URL);
      const response : any = await axios.get(url, { ...config, signal: abortControllerForSearchCLinkOwners.signal, params });

      const owners:string[] = response.data.owners;
      const cLinks = response.data.cLinks;
      const cLinkArray : (CLinkOwnerOption)[] = [];

      if (owners && owners.length) {
        owners.sort((a, b) => a.localeCompare(b));
        owners.forEach((owner:string) => {
          
          const cLinkOption: CLinkOwnerOption = Object();
          cLinkOption.key = owner;
          cLinkOption.id = owner;
          cLinkOption.record = owner;
          cLinkArray.push(cLinkOption);
          cLinks.forEach((cLinkRecord:CLinkRecord) => {
            if (cLinkRecord.owner !== owner) return;
            const cLinkOption: CLinkOwnerOption = Object();
            cLinkOption.key = owner;
            cLinkOption.id = owner;
            cLinkOption.record = cLinkRecord;
            cLinkArray.push(cLinkOption);
          });
        });
      } else {
        cLinks.forEach((cLinkRecord:CLinkRecord) => {
          const cLinkOption: CLinkOwnerOption = Object();
          cLinkOption.key = cLinkRecord.cLinkId;
          cLinkOption.id = cLinkRecord.cLinkId;
          cLinkOption.record = cLinkRecord;
          cLinkArray.push(cLinkOption);
        });
      }

      dto = {
        status : ResponseStatus.OK, 
        error : null,
        cLinksOwnerResponse : cLinkArray
      };
    } catch (error) {
      if (!axios.isCancel(error)) {
        const isLoggedOut = ErrorHandler.handleAuthError(error);
        if (!isLoggedOut) {
          const errData : ErrorData = ErrorHandler.getErrorMessage(error);
          dto = {
            status : ResponseStatus.ERROR, 
            error : errData,
            cLinksOwnerResponse : null
          };
        }
      }
    }
    return dto;
  }
};